/**
 * Login Page - Authentification Admin/Manager
 *
 * Page de connexion pour les utilisateurs back-office (ADMIN, MANAGER)
 * Les vendeurs (CASHIER) n'utilisent pas cette page, ils accèdent directement au POS
 * et se sélectionnent via VendorQuickBar
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { api } from '../services/api';
import { logger } from '../utils/logger';

// Liste des tenants disponibles
const AVAILABLE_TENANTS = [
  { slug: 'folies', name: 'Folies Maraîchères', city: 'Dour' },
  { slug: 'folies-test', name: 'Folies Boutik Test', city: 'Test' },
];

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, loading: authLoading, error: authError } = useAuth();
  const { tenant, setTenant, loading: tenantLoading } = useTenant();

  const [selectedTenantSlug, setSelectedTenantSlug] = useState(AVAILABLE_TENANTS[0].slug);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Rediriger si déjà authentifié
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, location]);

  // Charger le tenant sélectionné
  const handleTenantChange = async (slug: string) => {
    setSelectedTenantSlug(slug);
    setLoading(true);
    setError(null);

    try {
      const tenantData = await api.getTenantBySlug(slug);
      await setTenant(tenantData);
      logger.log('[Login] Tenant chargé:', tenantData.name);
    } catch (err) {
      logger.error('[Login] Erreur chargement tenant:', err);
      setError('Impossible de charger la boutique');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    // S'assurer que le tenant est chargé avant le login
    if (!tenant) {
      setError('Chargement de la boutique en cours...');
      return;
    }

    setLoading(true);

    try {
      logger.log('[Login] 🔑 Tentative de login avec tenant:', tenant.id);
      const success = await login(email, password);

      if (success) {
        logger.log('[Login] ✅ Login réussi, redirection vers Dashboard');
        // Redirection gérée par useEffect
      } else {
        setError(authError || 'Identifiants incorrects');
      }
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (tenantLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            🏪 {tenant?.name || 'Folies SAAS'}
          </h1>
          <p className="text-gray-600">Connexion Back-Office</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tenant Selector */}
            <div>
              <label htmlFor="tenant" className="block text-sm font-medium text-gray-700 mb-2">
                Boutique
              </label>
              <select
                id="tenant"
                value={selectedTenantSlug}
                onChange={(e) => handleTenantChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                disabled={loading}
              >
                {AVAILABLE_TENANTS.map((t) => (
                  <option key={t.slug} value={t.slug}>
                    {t.name} ({t.city})
                  </option>
                ))}
              </select>
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="admin@folies.com"
                disabled={loading}
                autoComplete="email"
                autoFocus
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Mot de passe
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="••••••••"
                disabled={loading}
                autoComplete="current-password"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-700 text-center">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !email || password.length < 6}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium text-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  Connexion...
                </span>
              ) : (
                'Se connecter'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">ou</span>
            </div>
          </div>

          {/* POS Access */}
          <button
            type="button"
            onClick={() => navigate('/pos')}
            className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            🛒 Accéder au POS (Vendeurs)
          </button>

          {/* Info */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-xs text-gray-600 text-center">
              <strong>Admin/Manager :</strong> Utilisez cette page
              <br />
              <strong>Vendeurs :</strong> Accédez directement au POS
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            Folies SAAS © 2025 - Tous droits réservés
          </p>
        </div>
      </div>
    </div>
  );
}
